import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ResumeUpload from "@/components/ResumeUpload";
import MathCaptcha from "@/components/MathCaptcha";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * PUBLIC job-application dialog — no login required, protected by a
 * math CAPTCHA. Used from the public "Open Positions" cards.
 */
export default function PublicApplyDialog({ job, open, onOpenChange, onSubmitted }) {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", resume_text: "", cover_note: "" });
  const [fileMeta, setFileMeta] = useState(null);
  const [captchaOk, setCaptchaOk] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setForm({ full_name: "", email: "", phone: "", resume_text: "", cover_note: "" });
      setFileMeta(null);
      setCaptchaOk(false);
    }
  }, [open]);

  const submit = async () => {
    if (!job) return;
    if (!form.full_name || !form.email || !form.phone || !form.resume_text) {
      toast.error("Name, email, phone and resume file are required");
      return;
    }
    if (!captchaOk) {
      toast.error("Please solve the captcha to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/applications", {
        job_id: job.job_id,
        ...form,
        resume_filename: fileMeta?.filename,
        resume_file_b64: fileMeta?.file_b64,
        resume_content_type: fileMeta?.content_type,
      });
      toast.success("Application submitted — our AI will match you against the role in a few moments.");
      onSubmitted?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl bg-offwhite border-navy/20 max-h-[92vh] overflow-y-auto"
        data-testid="public-apply-dialog"
      >
        <DialogHeader>
          <DialogTitle className="serif-display text-3xl">{job?.title || "Apply"}</DialogTitle>
          <DialogDescription className="text-navy/60">
            {job?.location} · {job?.industry}
          </DialogDescription>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Full name</Label>
            <Input
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              data-testid="public-apply-name"
            />
          </div>
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="public-apply-email"
            />
          </div>
          <div className="md:col-span-2">
            <Label>Phone (WhatsApp preferred)</Label>
            <Input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="public-apply-phone"
            />
          </div>
          <div className="md:col-span-2">
            <Label>Resume (PDF or DOCX)</Label>
            <div className="mt-2">
              <ResumeUpload
                value={form.resume_text}
                onChange={(t) => setForm({ ...form, resume_text: t })}
                onFileMeta={setFileMeta}
                testid="public-apply-resume"
              />
            </div>
          </div>
          <div className="md:col-span-2">
            <Label>Cover note (optional)</Label>
            <Textarea
              rows={3}
              value={form.cover_note}
              onChange={(e) => setForm({ ...form, cover_note: e.target.value })}
              data-testid="public-apply-cover"
            />
          </div>
          <div className="md:col-span-2">
            <MathCaptcha onValid={setCaptchaOk} testidPrefix="public-apply-captcha" />
          </div>
        </div>
        <DialogFooter>
          <button
            type="button"
            className="btn-outline"
            onClick={() => onOpenChange(false)}
            data-testid="public-apply-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={submit}
            disabled={submitting || !captchaOk}
            data-testid="public-apply-submit"
          >
            {submitting ? "Submitting…" : "Submit application"}
          </button>
        </DialogFooter>
        <p className="text-[10px] text-navy/40 mt-3 text-center">
          No login required. Your resume will be matched to the job description by our AI within a few minutes.
        </p>
      </DialogContent>
    </Dialog>
  );
}
